import React, {useEffect, useState} from "react";
import {BidBaseModel, BidStatus, blank_bid} from "./bidding_model";
import {useNavigate, useParams} from "react-router-dom";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "../ui/dataTable";
import {Card, CardContent, CardHeader, CardTitle,} from "../ui/card";
import {UserContext} from "../../contexts/UserContext";
import {moneyFormatter} from "../../lib/formatters";
import {useAuthInfo} from "@propelauth/react";
import {deleteWithToken, fetchWithToken, postWithToken} from "../../api/apiBase";
import {SortableHeader} from "../ui/tableUtils";
import {TrashIcon} from "lucide-react";
import {CompanyType} from "../../models/company";
import TableFilters, { Filter } from "../ui/TableFilter";
import { boolean, string } from 'zod';
import BiddingBuilderSummaryView from "./BiddingBuilderSummaryView";
import BiddingDeveloperSummaryView from "./BiddingDeveloperSummaryView";


export const _columns: ColumnDef<BidBaseModel>[] = [
    {
        header: ({column}) => <SortableHeader column={column} name={"Amount"}/>,
        accessorKey: "bid_amount",
        cell: ({row}) => {
            const amount = parseFloat(row.getValue("bid_amount"));
            const formatted = moneyFormatter.format(amount);
            return <div className="text-center font-medium ">{formatted}</div>;
        },
    },
    {
        header: "Title",
        accessorKey: "title",

    },
    {
        header: ({column}) => <SortableHeader column={column} name={"Created on"}/>,
        accessorKey: "created_at",
        cell: ({row}) => {
            const date = new Date(row.original.created_at);
            return <span>{date.toLocaleDateString()}</span>;
        },
    },
    {
        header: ({column}) => <SortableHeader column={column} name={"Status "}/>,
        accessorKey: "status",
    },
    {

        header: ({column}) => <SortableHeader column={column} name={"Response"}/>,
        accessorKey: "response_status",
    },
    {
        header: ({column}) => <SortableHeader column={column} name={"Submitted on"}/>,
        accessorKey: "submitted_at",
        cell: ({row}) => {
            if (row.original.submitted_at) {
                const date = new Date(row.original.submitted_at);
                return <span>{date.toLocaleDateString()}</span>;
            } else {
                return <></>;
            }
        },
    },
];

export const _columnsDeveloper: ColumnDef<BidBaseModel>[] = [

    {
        header: ({column}) => <SortableHeader column={column} name={"Amount"}/>,
        accessorKey: "bid_amount",
        cell: ({row}) => {
            const amount = parseFloat(row.getValue("bid_amount"));
            const formatted = moneyFormatter.format(amount);
            return <div className="text-center font-medium ">{formatted}</div>;
        },
    },
    {

        header: ({column}) => <SortableHeader column={column} name={"Company"}/>,
        accessorKey: "company_name",
    },
    {
        header: ({column}) => <SortableHeader column={column} name={"Response"}/>,
        accessorKey: "response_status",
    },
    {
        header: ({column}) => <SortableHeader column={column} name={"Submitted"}/>,
        accessorKey: "submitted_at",
        cell: ({row}) => {
            if (row.original.submitted_at) {
                const date = new Date(row.original.submitted_at);
                return <span>{date.toLocaleDateString()}</span>;
            } else {
                return <></>;
            }
        },
    },
];



type BidSummaryViewProps = {
    bids: BidBaseModel[]
}

export type BiddingProps = {
    tableData: any[];
    tableColumns: any[]
    handleRowClick: (string:any) => void
}

export type BiddingDeveloperProps = BiddingProps & {
    selectedRows?: string[]; 
  };

export function BidSummaryView(props: BidSummaryViewProps) {
    const navigate = useNavigate();
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<{ project_id: string }>();
    const [tableData, setTableData] = useState<BidBaseModel[]>(props.bids || [])
    const [isDeletedBid, setIsDeletedBid] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<string[]>([]);

    const userContext = React.useContext(UserContext);

    const onSelect = (event: React.MouseEvent<HTMLSpanElement>, rowData: any) => {
        event.preventDefault()
        event.stopPropagation()
        
        setSelectedRows((prev) => {
            const newSelectedRows = [...prev]; 
            const index = newSelectedRows.indexOf(rowData._id);
    
            if (index === -1) {
                newSelectedRows.push(rowData._id);
            } else {
                newSelectedRows.splice(index, 1);
            }
            return newSelectedRows;
        });
    }

    const _ds: ColumnDef<BidBaseModel>[] = [
        {
            header: "Delete",
            cell: ({row}) => {
                if (row.original.status === BidStatus.Draft) {
                    return <button
                    disabled={isDeletedBid}
                        // variant={"outline"}
                        onClick={(e) => {
                            e.stopPropagation()
                            onDeleteBid(row.original)
                        }}>
                        <TrashIcon width={15} height={15}/>
                    </button>

                } else {
                    return <></>
                }
            }
        }
    ]

    const selectCol: ColumnDef<BidBaseModel>[] = [
          {
        header: ({column}) => <SortableHeader column={column} name={"x"}/>,
        accessorKey: "selected",
        cell: ({row}) => {
            const isSelected = selectedRows.includes((row.original as any)._id);
            return <span
            className={`text-sm ${isSelected || false ? 'text-green-700 font-bold' : 'text-gray-600'} hover:font-bold`} 
            onClick={(event)=> onSelect(event, row.original)}>{isSelected ? 'selected' : 'select'}</span>;
        },
    },

    ]

    const onDeleteBid = (bid: BidBaseModel) => {
        setIsDeletedBid(true)
        deleteWithToken(`/projects/${project_id}/bidding/${bid.id}/delete`,
            accessToken as string)
            .then((res) => {
                console.log(bid)
                setTableData((prevData) => prevData.filter((item) => item.id !== bid.id));
            }).catch((err) => {

        }).finally(() => {
            setIsDeletedBid(false); 
        });
    }

    const handleRowCLick = (row: any) => {
        console.log(row.original)
        let row_id =  row.original._id ? row.original._id : row.original.id;
        row.original.project_id && row_id
            ? navigate(`/project/${row.original.project_id}/bid/${row_id}`)
            : navigate("");
    }
    
    const filterStatus = (selectedSorting: string[], data: any[]) => {
        const updatedData = data.filter((item) => 
            selectedSorting.includes(item.status) 
        );
        return updatedData
    };
    const filterResponseStatus = (selectedSorting: string[], data: any[]) => {
        const updatedSorting = selectedSorting.map(value =>
            value === "blank" ? null : value
        );
        const updatedData = data.filter((item) => 
            updatedSorting.includes(item.response_status)
        );
    
        return updatedData;
    };
    
    
    const tableFilters: Filter[] = [
        {responseStatus: {
              blank: filterResponseStatus,
              rejected: filterResponseStatus,
              in_review: filterResponseStatus,
              validated: filterResponseStatus,
            },
        ...(userContext?.company_type === CompanyType.builder && {
            status: {
                submitted: filterStatus,
                draft: filterStatus,
                accepted: filterStatus
            },
          })

    }
    ]

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <div className="flex columns-2 justify-between">
                        <div> Your Bids</div>
                        <div className="justify-end">

                        </div>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
            <div>
                <TableFilters availableFilters={tableFilters} data={props.bids} setData={setTableData}/></div>
                {userContext?.company_type === "developer" ? (
                    <BiddingDeveloperSummaryView tableData={tableData} tableColumns={[...selectCol,..._columnsDeveloper]} selectedRows={selectedRows} handleRowClick={handleRowCLick}/>

                ) : (
                    <BiddingBuilderSummaryView tableData={tableData} tableColumns={[..._columns, ..._ds]} handleRowClick={handleRowCLick}/>
                )}

            </CardContent>
        </Card>
    );
}


function BidSummary() {
    const {accessToken} = useAuthInfo()
    const [bids, setBids] = useState<BidBaseModel[] | null>(null)
    const {project_id} = useParams<{ project_id: string }>();

    useEffect(() => {
        fetchWithToken(`/projects/${project_id}/bidding/`, accessToken as string)
            .then((data) => {
                // console.log(data);
                setBids(data)
            })
            .catch((err) => {
                console.log(err);
            })
    }, [project_id])

    if (!bids) {
        return <></>;
    }
    return <BidSummaryView bids={bids}/>
}


export default BidSummary;
